import { Alert, Skeleton, Stack, Tooltip } from "@mui/material"
import numbro from "numbro"
import { useEffect, useState } from "react"

import { getOnchainPoolFeePercentage, getOnchainPoolReserves } from "../../api/pools-api"
import { getTotalSupply } from "../../api/token-api"
import { SectionInfo } from "../../components/SectionInfo"
import { SimpleNumberFormat } from "../../components/SimpleNumberFormat"
import { TokenBlock } from "../../components/TokenBlock"
import type { PoolReserves } from "../../types"
import { formatFullDate, formatRelative } from "../../utils/date-utils"
import { getTotalBurned } from "@/api/token-burner-api"
import { usePool } from "@/context/PoolContext"
import ENV from "@/env"
import { useTokenInfo } from "@/hooks/use-token-info"
import useLockedShare from "@/hooks/useLockedLiquidity"
import { formatNumberAuto, parseBigIntAsNumber } from "@/utils/format"

export function PoolDetailsCard() {
  const { pool } = usePool()

  const { created, id: poolId } = pool
  const { baseToken, quoteToken } = pool

  const [reserves, setReserves] = useState<PoolReserves | null>(null)
  const [lpTotalSupply, setLpTotalSupply] = useState<string | null>(null)
  const [lpBurned, setLpBurned] = useState<string | null>(null)

  const { data: lockedShare } = useLockedShare(poolId)

  useEffect(() => {
    if (!poolId || !pool) return
    getOnchainPoolReserves(pool).then(setReserves)
    getTotalSupply(poolId).then(setLpTotalSupply)
    getTotalBurned(ENV.VITE_TOKEN_BURNER_PROCESS, poolId).then(setLpBurned)
  }, [poolId, pool])

  const [lpToken] = useTokenInfo(poolId)

  const feePercentage = (pool.pool_fee_bps / 100).toFixed(2)

  return (
    <>
      {(baseToken !== pool.baseToken || quoteToken !== pool.quoteToken) && (
        <Alert severity="warning">This pool had its token pair updated since deployment.</Alert>
      )}
      <SectionInfo
        title="Created at"
        value={
          <Tooltip title={formatFullDate(new Date(created * 1000))}>
            <span>{formatRelative(new Date(created * 1000))}</span>
          </Tooltip>
        }
      />
      <SectionInfo title="LP Token" value={<TokenBlock tokenId={poolId} hideCopyToClipboard />} />

      {lpTotalSupply === null ? (
        <Skeleton height={20} />
      ) : (
        <SectionInfo
          title="LP Total Supply"
          value={formatNumberAuto(parseBigIntAsNumber(lpTotalSupply, lpToken?.denomination))}
        />
      )}

      {lpBurned === null ? (
        <Skeleton height={20} />
      ) : (
        <SectionInfo
          title="LP Burned"
          value={formatNumberAuto(parseBigIntAsNumber(lpBurned, lpToken?.denomination))}
        />
      )}

      {reserves === null ? (
        <Skeleton height={44} />
      ) : (
        <>
          <SectionInfo
            title="Reserves"
            value={
              <Stack gap={0.5} alignItems="flex-end">
                <SimpleNumberFormat
                  amount={reserves.baseToken}
                  tokenId={baseToken}
                  needsParsing
                  currency={false}
                />
                <SimpleNumberFormat
                  amount={reserves.quoteToken}
                  tokenId={quoteToken}
                  needsParsing
                  currency={false}
                />
              </Stack>
            }
          />
        </>
      )}
      {feePercentage === null ? (
        <Skeleton height={20} />
      ) : (
        <SectionInfo title="Fee Percentage" value={`${feePercentage}%`} />
      )}
      {!lockedShare ? (
        <Skeleton height={20} />
      ) : (
        <SectionInfo
          title="Locked Liquidity"
          value={numbro(lockedShare?.["One-Year-Locked-Liquidity"] || 0).formatCurrency({
            mantissa: 0,
            thousandSeparated: true,
          })}
        />
      )}
    </>
  )
}
